import React, { useCallback, useEffect, useState } from "react";

import { PaymentDetails } from "./payment_details";
import { TermsAndConditions } from "./terms_and_conditions";
import { PayButton } from "./pay_button";
import { DividerHeader } from "./divider_header";
import { VerifyPhoneNumberModal } from "./verify_phone_number_modal";
import { Stripe, StripeElements, StripeElementsOptions } from "@stripe/stripe-js";
import { Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { QueryClient, QueryClientProvider, useMutation } from "@tanstack/react-query";
import { submitPayment, SubmitPaymentParams, STRIPE_MINIMUM_CHARGE } from "src/checkout/submit_payment";
import { parsePaymentMethods } from "./decoders";
import { PaymentMethod } from "./model";
import { createPaymentMethod } from "./create_payment_method";
import { ReactComponent as AlertCircleIcon } from "images/icons/alert-circle.svg";
const queryClient = new QueryClient();

const Loading = () => {
  return (
    <section className="container width-constraint-narrow space-ml-none">
      <div className="skeleton is-heading"></div>
      <div className="skeleton is-heading"></div>
      <div className="skeleton is-heading"></div>
      <div className="skeleton is-heading"></div>
      <div className="skeleton is-paragraph" style={{ "--lines": 1 } as React.CSSProperties}></div>
    </section>
  );
}

const Checkout = ({
  paymentAmountInCents,
  formElement,
  billingName,
  billingEmail,
  shouldTakePayment,
  allowSaveCard,
  enabledPaymentMethods,
  confirmPath,
  changePhoneNumberPath,
  stripe = null,
  elements = null,
} : {
  paymentAmountInCents?: number;
  formElement: HTMLFormElement;
  billingName: string;
  billingEmail: string;
  shouldTakePayment: boolean;
  allowSaveCard: boolean;
  enabledPaymentMethods: string[];
  confirmPath: string;
  changePhoneNumberPath: string;
  stripe: Stripe | null;
  elements: StripeElements | null;
}) => {

  const [savedPaymentMethods, setPaymentMethods] = useState<PaymentMethod[] | undefined>(undefined);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | undefined>(undefined);
  const [paymentDetailsComplete, setPaymentDetailsComplete] = useState<boolean>(false);
  const [termsAndConditionsChecked, setTermsAndConditionsChecked] = useState<boolean>(false);
  const [saveCard, setSaveCard] = useState<boolean>(allowSaveCard);
  const [verifyPhoneNumber, setVerifyPhoneNumber] = useState<string | undefined>(undefined);
  const [verifyMessageType, setVerifyMessageType] = useState<string | undefined>(undefined);
  const [verifyError, setVerifyError] = useState<string | undefined>(undefined);

  const {
    mutate: submitPaymentMutation,
    isPending: isLoadingSubmitPayment,
    isSuccess: isSuccessPayment,
    isError: isErrorSubmitPayment,
    error: errorSubmitPayment,
    reset: resetSubmitPayment
  } = useMutation<void, Error, SubmitPaymentParams>( { mutationFn: submitPayment })

  const fetchPaymentMethods = async () : Promise<PaymentMethod[]> => {
    const resp = await fetch("/settings/payment_methods", {
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json"
      }
    });

    if (!resp.ok)
      return [];

    const json = await resp.json();
    return parsePaymentMethods(json);
  }

  useEffect(() => {
    fetchPaymentMethods().then((paymentMethods: PaymentMethod[]) => {
      if (paymentMethods.length > 0) {
        const orderedPaymentMethodsByCardDefault = paymentMethods.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
        setSelectedPaymentMethod(orderedPaymentMethodsByCardDefault[0]);
        setPaymentDetailsComplete(true)
      }

      setPaymentMethods(paymentMethods);
    });
  }, [setPaymentMethods]);

  const handlePaymentSubmit = useCallback(async () => {
    if (paymentAmountInCents === undefined) throw new Error("Trying to confirm an order without an order amount");

    setVerifyError(undefined);

    if (shouldTakePayment) {
      if (selectedPaymentMethod === undefined && paymentDetailsComplete) {
        if (!stripe || !elements)
          throw new Error("Stripe can't be found during paymentMethod creation");

        const newPaymentMethod = await createPaymentMethod({
          stripe: stripe,
          elements: elements,
          billingName: billingName,
          billingEmail: billingEmail,
          saveCard: saveCard,
        });

        submitPaymentMutation({
          stripe: stripe,
          paymentAmountInCents: paymentAmountInCents,
          paymentMethod: newPaymentMethod,
          confirmPath: confirmPath,
          formData: new FormData(formElement),
          handleVerifyPhoneNumber: handleVerifyPhoneNumber,
        });
      } else if (selectedPaymentMethod !== undefined) {
        submitPaymentMutation({
          stripe: stripe,
          paymentAmountInCents: paymentAmountInCents,
          paymentMethod: selectedPaymentMethod,
          confirmPath: confirmPath,
          formData: new FormData(formElement),
          handleVerifyPhoneNumber: handleVerifyPhoneNumber,
        });
      } else {
        throw new Error("No saved method or new payment method specified during confirm");
      }
    } else {
      submitPaymentMutation({
        stripe: null,
        paymentAmountInCents: paymentAmountInCents,
        confirmPath: confirmPath,
        formData: new FormData(formElement),
        handleVerifyPhoneNumber: handleVerifyPhoneNumber,
      });
    }
  }, [
      billingEmail, billingName, elements, formElement,
      confirmPath, paymentAmountInCents, paymentDetailsComplete,
      selectedPaymentMethod, shouldTakePayment, stripe,
      submitPaymentMutation, saveCard
    ]);
  

  const header = () => {
    if (shouldTakePayment)
      return <DividerHeader />

    return null;
  };

  const updatePaymentDetailsComplete = (isComplete: boolean) => {
    setPaymentDetailsComplete(isComplete);
  }

  const setSelectedPaymentMethodHandler = async (newPaymentMethod: PaymentMethod) => {
    setSelectedPaymentMethod(newPaymentMethod);

    return undefined;
  }

  const paymentDetails = () => {
    if (!shouldTakePayment || !savedPaymentMethods)
        return null;

    return (
      <PaymentDetails
        savedPaymentMethods={savedPaymentMethods}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethodHandler}
        setPaymentDetailsComplete={updatePaymentDetailsComplete}
        billingName={billingName}
        billingEmail={billingEmail}
        allowSaveCard={allowSaveCard}
        onSaveCardChange={setSaveCard}
        enabledPaymentMethods={enabledPaymentMethods}
      />
    );
  };

  const divider = () => {
    if (shouldTakePayment)
      return <hr />;

    return null;
  }

  const submitPaymentError = () => {
    if (!isErrorSubmitPayment) return null;

    return (
      <section className="container width-constraint-narrow space-ml-none">
        <div className="callout has-border">
          <AlertCircleIcon className="icon streamline-icon" />
          <div>{errorSubmitPayment.message}</div>
        </div>
      </section>
    )
  }

  const handleVerifyPhoneNumber = (verifyPhoneNumber: string, verifyMessageType: string | undefined, error: string) => {
    setVerifyPhoneNumber(verifyPhoneNumber);
    setVerifyMessageType(verifyMessageType);
    setVerifyError(error == "" ? undefined : error);
  }

  const cancelVerifyPhoneNumber = () => {
    setVerifyPhoneNumber(undefined);
    setVerifyMessageType(undefined);
    setVerifyError(undefined);
    resetSubmitPayment();
  }

  if (!savedPaymentMethods) return <Loading></Loading>;

  const validPaymentMethod = paymentDetailsComplete && (selectedPaymentMethod === undefined || !selectedPaymentMethod.hasExpired());
  const isReadyToPay = (validPaymentMethod || !shouldTakePayment) && termsAndConditionsChecked && !isLoadingSubmitPayment && !isSuccessPayment;

  return (
    <>
      {header()}

      {paymentDetails()}
      {verifyPhoneNumber && !isErrorSubmitPayment ? <VerifyPhoneNumberModal phoneNumber={verifyPhoneNumber} messageType={verifyMessageType} changePhoneNumberPath={changePhoneNumberPath} isPending={isLoadingSubmitPayment} error={verifyError} clearError={() => setVerifyError(undefined)} submit={handlePaymentSubmit} cancel={cancelVerifyPhoneNumber} /> : <></>}

      <section className="section">
        {submitPaymentError()}

        {divider()}

        <div className="container">
          <TermsAndConditions checked={termsAndConditionsChecked} setTermsAndConditionsChecked={setTermsAndConditionsChecked} />
          <PayButton shouldTakePayment={shouldTakePayment} paymentType={selectedPaymentMethod?.type} enabled={isReadyToPay} handlePaymentSubmit={handlePaymentSubmit} />
        </div>
      </section>
    </>
  );
}

const CheckoutContainer = ({
  paymentAmountInCents,
  stripe,
  formElement,
  afterpayEnabled,
  billingName,
  billingEmail,
  allowSaveCard,
  confirmPath,
  changePhoneNumberPath,
} : {
  paymentAmountInCents: number;
  stripe: Stripe | null;
  formElement: HTMLFormElement;
  afterpayEnabled: boolean;
  billingName: string;
  billingEmail: string;
  allowSaveCard: boolean;
  confirmPath: string;
  changePhoneNumberPath: string;
}) => {
  const enabledPaymentMethods = () : string[] => {
    return ["card"].concat(afterpayEnabled ? ["afterpay_clearpay"] : []);
  }

  const elementOptions : StripeElementsOptions = {
    paymentMethodCreation: 'manual',
    mode: 'payment',
    currency: 'nzd',
    amount: paymentAmountInCents,
    paymentMethodTypes: enabledPaymentMethods(),
  };

  const shouldTakePayment = paymentAmountInCents >= STRIPE_MINIMUM_CHARGE;

  const params = {
    formElement: formElement,
    paymentAmountInCents: paymentAmountInCents,
    billingName: billingName,
    billingEmail: billingEmail,
    shouldTakePayment: shouldTakePayment,
    allowSaveCard: allowSaveCard,
    enabledPaymentMethods: enabledPaymentMethods(),
    confirmPath: confirmPath,
    changePhoneNumberPath: changePhoneNumberPath,
  };

  const CheckoutInner = () => {
    return (
      <Checkout {...params} stripe={useStripe()} elements={useElements()}></Checkout>
    );
  }


  if (shouldTakePayment) {
    return (
      <QueryClientProvider client={queryClient}>
        <Elements stripe={stripe} options={elementOptions}>
          <CheckoutInner />
        </Elements>
      </QueryClientProvider>
    );
  } else {
    return (
      <QueryClientProvider client={queryClient}>
        <Checkout {...params} stripe={null} elements={null}></Checkout>
      </QueryClientProvider>
    );
  }
}

export { CheckoutContainer, Checkout }
